import { signal } from '@preact/signals'

export interface SessionUser {
  email: string
  id: string
  nickname: string
}

interface SessionResponse {
  accessToken: string
  refreshToken: string
  user: SessionUser
}

export const sessionUser = signal<SessionUser | null>(null)

let accessToken: string | null = null

export function getAccessToken(): string | null {
  return accessToken
}

export interface AuthResult {
  error?: string
  ok: boolean
}

async function readError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string }

    return body.error ?? 'Something went wrong. Please try again.'
  } catch {
    return 'Something went wrong. Please try again.'
  }
}

async function apiFetch(path: string, init: RequestInit = {}, retry = true): Promise<Response> {
  const headers = new Headers(init.headers)
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }

  const response = await fetch(`${process.env.BUN_PUBLIC_API_HTTP_ENDPOINT}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  })

  if (response.status === 401 && retry) {
    const refreshed = await refreshSession()
    if (refreshed) {
      return apiFetch(path, init, false)
    }
  }

  return response
}

function applySession(data: SessionResponse) {
  accessToken = data.accessToken
  sessionUser.value = data.user
}

function clearSession() {
  accessToken = null
  sessionUser.value = null
}

// Refreshes the session from the httpOnly cookie. Also used on page load.
export async function refreshSession(): Promise<boolean> {
  try {
    const response = await fetch(`${process.env.BUN_PUBLIC_API_HTTP_ENDPOINT}/auth/refresh`, {
      credentials: 'include',
      method: 'POST',
    })
    if (!response.ok) {
      clearSession()

      return false
    }

    applySession((await response.json()) as SessionResponse)

    return true
  } catch {
    clearSession()

    return false
  }
}

export async function signup(nickname: string, email: string, password: string): Promise<AuthResult> {
  try {
    const response = await apiFetch('/auth/signup', {
      body: JSON.stringify({ email, nickname, password }),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    })
    if (!response.ok) {
      return { error: await readError(response), ok: false }
    }

    applySession((await response.json()) as SessionResponse)

    return { ok: true }
  } catch {
    return { error: 'Could not reach the server. Please try again.', ok: false }
  }
}

export async function signin(email: string, password: string): Promise<AuthResult> {
  try {
    const response = await apiFetch('/auth/login', {
      body: JSON.stringify({ email, password }),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    })
    if (!response.ok) {
      return { error: await readError(response), ok: false }
    }

    applySession((await response.json()) as SessionResponse)

    return { ok: true }
  } catch {
    return { error: 'Could not reach the server. Please try again.', ok: false }
  }
}

export async function signout(): Promise<void> {
  try {
    await apiFetch('/auth/logout', { credentials: 'include', method: 'POST' })
  } catch {
    // Sign-out is idempotent server-side; local state is cleared regardless.
  }
  clearSession()
}
